import type { RefObject } from "react";
import { useEffect, useRef } from "react";

const BASE_ANIMATION_DURATION = 1000;
const MIN_SPEED_FACTOR = 0.5;
const DEFAULT_SPEED_FACTOR = 1;

interface IHookProps {
    texts: readonly string[];
    speedFactor?: number;
    enabled?: boolean;
}

const getRandomIndex = (exclude: number, texts: readonly string[]) => {
    let newIndex = exclude;
    while (newIndex === exclude) {
        newIndex = Math.floor(Math.random() * texts.length);
    }
    return newIndex;
};

export const useTextAnimation = ({
    texts,
    speedFactor = DEFAULT_SPEED_FACTOR,
    enabled = true,
}: IHookProps): RefObject<HTMLSpanElement | null> => {
    const animatedTextElRef = useRef<HTMLSpanElement>(null);

    const restrictedSpeedFactor = speedFactor < MIN_SPEED_FACTOR ? MIN_SPEED_FACTOR : speedFactor;

    useEffect(() => {
        if (!enabled) {
            return;
        }
        const subTextElement = animatedTextElRef.current;
        if (!subTextElement || texts.length === 1) {
            return;
        }

        let currentIndex = 0;
        let timeout: ReturnType<typeof setTimeout>;
        let animation: Animation | undefined;

        const animationDuration = BASE_ANIMATION_DURATION * restrictedSpeedFactor;

        const interval = setInterval(() => {
            const keyframes: Keyframe[] = [
                { opacity: 1, transform: "translateX(0px)", offset: 0 },
                { opacity: 0, transform: "translateX(-50px)", offset: 0.5 },
                { opacity: 0, transform: "translateX(0px)", offset: 0.75 },
                { opacity: 1, transform: "translateX(0px)", offset: 1 },
            ];

            animation = subTextElement.animate(keyframes, { duration: animationDuration });
            timeout = setTimeout(() => {
                const nextIndex = getRandomIndex(currentIndex, texts);
                currentIndex = nextIndex;
                subTextElement.textContent = texts[currentIndex];
            }, animationDuration / 2);
        }, animationDuration * 2);

        return () => {
            clearInterval(interval);
            clearTimeout(timeout);
            animation?.cancel();
        };
    }, [texts, restrictedSpeedFactor, enabled]);

    return animatedTextElRef;
};
